'use client';

import type {
  AdaptiveScanningPhase,
  AdaptiveScanningStateDto,
} from '@terra/shared-contracts-applications';
import { ViewIcon } from '@terra/ui-icons';
import { HStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { Button } from '../../components/button';
import { IconButton } from '../../components/icon-button';
import { Tooltip } from '../../components/tooltip';
import { SettingsBooleanControl } from '../settings/SettingsBooleanControl';
import { SettingsCard } from '../settings/SettingsCard';
import { SettingsRow } from '../settings/SettingsRow';

const PHASE_LABEL_KEYS: Readonly<
  Record<
    AdaptiveScanningPhase,
    | 'phasePaused'
    | 'phaseVerifying'
    | 'phaseDiscovering'
    | 'phaseAttackReady'
    | 'phaseAttacking'
  >
> = {
  paused_blocked: 'phasePaused',
  resume_verification: 'phaseVerifying',
  discovering: 'phaseDiscovering',
  attack_ready: 'phaseAttackReady',
  attacking: 'phaseAttacking',
};

export type AdaptiveScanningCardProps = {
  state: AdaptiveScanningStateDto;
  canToggle?: boolean;
  isToggling?: boolean;
  isResuming?: boolean;
  onToggle: (enabled: boolean) => void;
  onResumeClick: () => void;
  onViewBlocked: () => void;
};

const getResumeMinutes = (autoResumeAt: string | null): number | null =>
  autoResumeAt
    ? Math.max(
        0,
        Math.ceil((new Date(autoResumeAt).getTime() - Date.now()) / 60_000),
      )
    : null;

export function AdaptiveScanningCard({
  state,
  canToggle = true,
  isToggling = false,
  isResuming = false,
  onToggle,
  onResumeClick,
  onViewBlocked,
}: AdaptiveScanningCardProps) {
  const t = useTranslations('designSystem.adaptiveScanning');
  const tCommon = useTranslations('designSystem.common');
  const tBlocked = useTranslations(
    'designSystem.signals.lifecycle.status.blockedReason',
  );
  const isPaused = state.phase === 'paused_blocked';
  const phaseLabel = state.phase
    ? t(PHASE_LABEL_KEYS[state.phase])
    : t('emptyState');
  const reasonLabel = state.pausedReason ? tBlocked(state.pausedReason) : null;
  const phaseValue =
    isPaused && reasonLabel ? `${phaseLabel} · ${reasonLabel}` : phaseLabel;
  const resumeMinutes = getResumeMinutes(state.autoResumeAt);
  const description = [
    phaseValue,
    resumeMinutes !== null ? t('resumesIn', { minutes: resumeMinutes }) : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
  const isOn = state.enabled;
  const showDetails = isOn && state.phase !== null;

  return (
    <SettingsCard>
      <SettingsRow
        label={t('title')}
        description={isOn ? description : undefined}
        tooltip={t('titleTooltip')}
        control={
          canToggle ? (
            <HStack gap={2} alignItems="center">
              {isPaused && isOn ? (
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={onResumeClick}
                  disabled={isResuming}
                >
                  {t('resume')}
                </Button>
              ) : null}
              <SettingsBooleanControl
                checked={isOn}
                label={isOn ? tCommon('yes') : tCommon('no')}
                disabled={isToggling || isResuming}
                onCheckedChange={(details) => {
                  if (typeof details.checked !== 'boolean') {
                    return;
                  }

                  onToggle(details.checked);
                }}
              />
            </HStack>
          ) : undefined
        }
        value={canToggle || state.phase === null ? undefined : phaseValue}
      />
      {showDetails ? (
        <SettingsRow
          label={t('aggressiveness')}
          tooltip={t('aggressivenessTooltip')}
          value={state.aggressiveness}
        />
      ) : null}
      {showDetails ? (
        <SettingsRow
          label={t('tier')}
          tooltip={t('tierTooltip')}
          value={String(state.unlockedTier)}
        />
      ) : null}
      {showDetails ? (
        <SettingsRow
          label={t('waveSize')}
          tooltip={t('waveSizeTooltip')}
          value={String(state.waveSize)}
        />
      ) : null}
      {showDetails ? (
        <SettingsRow
          label={t('lastResult')}
          tooltip={t('lastResultTooltip')}
          value={
            <HStack gap={2} alignItems="center">
              <Tooltip
                title={t('viewBlocked')}
                positioning={{ strategy: 'fixed' }}
              >
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label={t('view')}
                  onClick={onViewBlocked}
                >
                  <ViewIcon size={16} />
                </IconButton>
              </Tooltip>
              {String(state.lastWave?.terminals.length ?? 0)}
            </HStack>
          }
        />
      ) : null}
    </SettingsCard>
  );
}
